import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useContext, useRef } from 'react';

import { useBoardId, useCardId } from '@trello/id-context';

import { useCardStickerCountAndLimitFragment } from 'app/src/components/CardCover';
import { CardFrontBadges } from 'app/src/components/CardFrontBadges';
import { MirrorCardBoardChip } from 'app/src/components/MirrorCard/MirrorCardBoardChip';
import { CardAgingUpdater } from './CardAgingUpdater';
import { CardFrontAttachmentDropTarget } from './CardFrontAttachmentDropTarget';
import { CardFrontBoardHint } from './CardFrontBoardHint';
import { CardFrontContext } from './CardFrontContext';
import { CardFrontCover } from './CardFrontCover';
import { CardFrontLabels } from './CardFrontLabels';
import { CardFrontMembers } from './CardFrontMembers';
import { CardFrontNameWithStatusControl } from './CardFrontNameWithStatusControl';
import { useCardAsDropTarget } from './useCardAsDropTarget';

import styles from './TrelloCard.less';

export interface TrelloCardProps {
  name: string;
  url: string;
}

export const TrelloCard: FunctionComponent<TrelloCardProps> = ({
  name,
  url,
}) => {
  const boardId = useBoardId();
  const cardId = useCardId();
  const cardFrontDetailsRef = useRef<HTMLDivElement>(null);

  const { data } = useCardStickerCountAndLimitFragment({
    from: { id: cardId },
    optimistic: true,
  });
  const stickerCount = data?.stickers?.length ?? 0;
  const stickerLimit = data?.limits?.stickers?.perCard?.disableAt ?? 70; // default to 70 stickers on a card
  useCardAsDropTarget(stickerCount, stickerLimit, cardFrontDetailsRef);

  const { showBoardInfo } = useContext(CardFrontContext);

  return (
    <>
      <CardFrontAttachmentDropTarget />
      <CardFrontCover cardFrontDetailsRef={cardFrontDetailsRef} />
      <div
        className={cx(styles.details, {
          [styles.stickersBackdrop]: stickerCount > 0,
        })}
        ref={cardFrontDetailsRef}
      >
        {showBoardInfo && (
          // TODO: Rename to CardFrontBoardChip, rename isPaid -> showListName
          <MirrorCardBoardChip isPaid={false} isCompact={true} />
        )}

        <CardFrontLabels />
        <CardFrontNameWithStatusControl name={name} url={url} />
        <CardFrontBadges />
        <CardFrontMembers />
      </div>
      {showBoardInfo && <CardFrontBoardHint boardId={boardId} />}
      <CardAgingUpdater />
    </>
  );
};
